import { useCallback, useEffect, useRef, useState } from 'react';
import { checkMaintenanceOver, useMaintenance } from '../api/maintenance';
import { backByTime } from '../format';

/** How often to ask whether the till is back while the screen is up. */
const POLL_MS = 30_000;

const DEFAULT_MESSAGE = 'We are doing some maintenance. Please check back soon.';

/**
 * The till is switched off for maintenance.
 *
 * Drawn OVER the app rather than instead of it, and that is the whole point:
 * a half-rung bill lives in the billing screen's state, and swapping the app
 * out for this screen would unmount it and lose every book already scanned.
 * Left underneath, the bill is exactly where staff left it when the server
 * comes back.
 *
 * There is no close button. The only way past is maintenance ending, which is
 * checked every half minute, whenever the tab comes back into view, and on
 * "Try again".
 */
export default function MaintenanceScreen() {
  const { active, message, endsAt } = useMaintenance();
  const [checking, setChecking] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const retryRef = useRef<HTMLButtonElement>(null);
  const inFlight = useRef(false);

  const check = useCallback(async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    setChecking(true);
    try {
      await checkMaintenanceOver();
    } finally {
      inFlight.current = false;
      setChecking(false);
    }
  }, []);

  useEffect(() => {
    if (!active) return;
    const timer = window.setInterval(() => void check(), POLL_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible') void check();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [active, check]);

  // Keep the keyboard — and so the barcode scanner — on this screen. A scan
  // that landed in the search box behind would quietly add a book to a bill
  // nobody can see; and an Escape must not close the confirm dialog under it.
  useEffect(() => {
    if (!active) return;
    const onFocusIn = (e: FocusEvent) => {
      if (!panelRef.current?.contains(e.target as Node)) retryRef.current?.focus();
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (!panelRef.current?.contains(e.target as Node)) e.preventDefault();
      e.stopPropagation();
    };
    document.addEventListener('focusin', onFocusIn);
    window.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('focusin', onFocusIn);
      window.removeEventListener('keydown', onKeyDown, true);
    };
  }, [active]);

  // A disabled button drops focus; hand it back once the check is done.
  useEffect(() => {
    if (active && !checking) retryRef.current?.focus();
  }, [active, checking]);

  if (!active) return null;

  const backBy = backByTime(endsAt);

  return (
    <div className="maintenance-backdrop">
      <div
        ref={panelRef}
        className="maintenance"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="maintenance-title"
        aria-describedby="maintenance-message"
      >
        <h2 id="maintenance-title" className="maintenance-title">
          The till is closed for maintenance
        </h2>
        <p id="maintenance-message" className="maintenance-message">
          {message?.trim() || DEFAULT_MESSAGE}
        </p>
        {backBy && <p className="maintenance-back">Expected back by {backBy}</p>}
        <p className="muted small">
          Any bill on the till is kept as it is. If a sale was being completed, it was not
          saved — complete it again once the till is back.
        </p>
        <button
          type="button"
          ref={retryRef}
          className="btn btn-primary btn-block"
          onClick={() => void check()}
          disabled={checking}
        >
          {checking ? 'Checking…' : 'Try again'}
        </button>
      </div>
    </div>
  );
}
